import {
  Button,
  Checkbox,
  Divider,
  Flex,
  Form,
  Grid,
  Input,
  Popover,
  Typography,
} from "antd";
import { createUserWithEmailAndPassword, getAuth } from "firebase/auth";
import { useState } from "react";
import { FaRegCheckCircle, FaRegTimesCircle } from "react-icons/fa";
import { MdMailOutline } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { SignInGoogle } from "../../components/signin-flow/SignInGoogle";
import { useToken } from "../../hooks/useToken";
import { ThemeSwitcher, useTheme } from "../../providers/ThemeProvider";

const { useBreakpoint } = Grid;
const { Text, Title, Link } = Typography;

export function SignUp() {
  const { message, theme } = useTheme();
  const { token } = useToken();
  const screens = useBreakpoint();
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [isCreatingUser, setCreatingUser] = useState<boolean>(false);

  const password = Form.useWatch("password", form) || "";

  return (
    <div
      style={{
        width: screens.lg ? "50%" : "100%",
        minWidth: "320px",
        height: "100%",
        backgroundColor: token.colorBgContainer,
        display: "flex",
        alignItems: "center",
      }}
    >
      <div
        style={{
          margin: "0 auto",
          padding: screens.md
            ? `${token.paddingXL}px`
            : `${token.sizeXXL}px ${token.padding}px`,
          width: "380px",
        }}
      >
        <div style={{ position: "absolute", top: "20px", left: "46.5%" }}>
          <ThemeSwitcher />
        </div>
        <div
          style={{
            marginBottom: token.marginXL,
            display: "flex",
            justifyContent: "center",
          }}
        >
          <img
            src={
              theme == "DARK"
                ? "/assets/logo_dark.png"
                : "/assets/logo_white.png"
            }
            alt=""
            style={{
              width: "97px",
            }}
          />
        </div>
        <div
          style={{
            marginBottom: token.marginXL,
          }}
        >
          <Title
            style={{
              fontSize: screens.md
                ? token.fontSizeHeading2
                : token.fontSizeHeading3,
              textAlign: "center",
            }}
          >
            Sign up
          </Title>
          <Text
            style={{
              color: token.colorTextSecondary,
              display: "flex",
              textAlign: "center",
            }}
          >
            Start your journey with Zeitfy. Create an account.
          </Text>
        </div>
        <Form
          form={form}
          name="normal_login"
          initialValues={{
            remember: true,
          }}
          onFinish={({ email, password, terms }) => {
            console.log(email, password, terms);
            if (!email) {
              return message.error("Email is required");
            }

            if (!password) {
              return message.error("Password is required");
            }

            if (!terms) {
              return message.error(
                "You must agree to the terms and conditions"
              );
            }

            if (!/[a-z]/.test(password)) {
              return message.error(
                "Password must contain at least one lowercase letter"
              );
            }

            if (!/[A-Z]/.test(password)) {
              return message.error(
                "Password must contain at least one uppercase letter"
              );
            }

            if (!/[0-9]/.test(password)) {
              return message.error("Password must contain at least one number");
            }

            if (!/[@%:;.,]/.test(password)) {
              return message.error(
                "Password must contain at least one special character"
              );
            }

            if (password.length < 8) {
              return message.error("Password must be at least 8 characters");
            }

            if (isCreatingUser) return;

            setCreatingUser(true);

            createUserWithEmailAndPassword(getAuth(), email, password)
              .then((userCredential) => {
                navigate("/");
                setCreatingUser(false);
              })
              .catch((error) => {
                const errorCode = error.code;
                const errorMessage = error.message;

                if (errorCode === "auth/email-already-in-use") {
                  message.error("Email already in use");
                } else {
                  message.error(errorMessage);
                }

                setCreatingUser(false);
              });
          }}
          layout="vertical"
          requiredMark="optional"
        >
          <Form.Item
            name="email"
            label="Email"
            rules={[
              {
                type: "email",
                required: true,
                message: "Please input your Email!",
              },
            ]}
          >
            <Input
              prefix={<MdMailOutline color={token.colorIcon} size="16px" />}
              placeholder="Enter your Email"
            />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[
              {
                required: true,
                message: "Please input your Password!",
              },
            ]}
          >
            <Popover
              content={
                <Flex style={{ flexDirection: "column" }}>
                  <Text
                    style={{
                      color: token.colorTextHeading,
                      fontWeight: token.fontWeightStrong,

                      marginBottom: token.marginSM,
                    }}
                  >
                    Password must contain
                  </Text>

                  <FieldValidation
                    isMatch={/[a-z]/.test(password)}
                    text="At least one lowercase letter"
                  />

                  <FieldValidation
                    isMatch={/[A-Z]/.test(password)}
                    text="At least one uppercase letter"
                  />

                  <FieldValidation
                    isMatch={/[0-9]/.test(password)}
                    text="At least one number"
                  />

                  <FieldValidation
                    isMatch={/[@%:;.,]/.test(password)}
                    text="At least one special character"
                  />

                  <FieldValidation
                    isMatch={password.length >= 8}
                    text="Be at least 8 characters"
                  />
                </Flex>
              }
              placement="rightTop"
              trigger="focus"
              overlayClassName="passwordValidation"
            >
              <Input.Password
                type="password"
                placeholder="Password"
                onChange={(e) => form.setFieldValue("password", e.target.value)}
              />
            </Popover>
          </Form.Item>
          <Form.Item
            style={{ marginBottom: 0 }}
            rules={[
              {
                required: true,
                message: "Please agree with our terms",
              },
            ]}
            name="terms"
            valuePropName="checked"
            noStyle
          >
            <Checkbox>I have read and agree to the</Checkbox>
          </Form.Item>

          <Form.Item style={{ marginTop: "-5px" }}>
            <Link
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();

                navigate("/terms");
              }}
              href="/terms"
              style={{
                color: token.colorPrimary,
                fontSize: token.fontSize,
              }}
            >
              Terms and Conditions
            </Link>
          </Form.Item>

          <Form.Item style={{ marginBottom: "0px" }}>
            <Button
              block={true}
              loading={isCreatingUser}
              type="primary"
              htmlType="submit"
              style={{ boxShadow: `0 2px 0 ${token.boxShadow}` }}
            >
              Continue
            </Button>
            <div
              style={{
                marginTop: token.marginLG,
                textAlign: "center",
                width: "100%",
              }}
            >
              <Text
                style={{
                  color: token.colorTextSecondary,
                }}
              >
                Already have an account?
              </Text>{" "}
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();

                  navigate("/signin");
                }}
                href="/signin"
                style={{
                  color: token.colorPrimary,
                }}
              >
                Log in
              </Link>
            </div>
          </Form.Item>
        </Form>

        <Flex align="center" gap="24px">
          <Divider style={{ maxWidth: "126px", minWidth: "unset" }} />
          <Text style={{ whiteSpace: "nowrap" }}>OR</Text>
          <Divider style={{ maxWidth: "126px", minWidth: "unset" }} />
        </Flex>

        <SignInGoogle />
      </div>
    </div>
  );
}

function FieldValidation({
  isMatch,
  text,
}: {
  isMatch: boolean;
  text: string;
}) {
  const { token } = useToken();

  return (
    <Flex align="center" gap="8px">
      {isMatch && <FaRegCheckCircle color={token.colorSuccess} />}
      {!isMatch && <FaRegTimesCircle color={token.colorTextSecondary} />}
      <Text
        style={{ color: isMatch ? token.colorText : token.colorTextSecondary }}
      >
        {text}
      </Text>
    </Flex>
  );
}
